import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
} from "react";
import useFetchExecutionSubGroup from "../../../hooks/useFetchExecutionSubGroup";
import { Autocomplete, TextField } from "@mui/material";

interface IAutoCompleteExecutionSubGroup {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handelOnChangeExecutionSubGroup: (
    event: ChangeEvent<any>,
    value: any,
  ) => void;
  currentExecutionSubGroupId: string | number | null;
  sx?: React.CSSProperties;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  listLabel?: string;
  placeholder?: string;
  includeExecutionSubGroupId?: boolean;
  lazyLoad?: boolean;
}

/**
 * All execution sub groups with auto complete dropdown functionality
 * @returns
 * Selected execution sub group with id and execution_sub_group_code
 */
const AutoCompleteExecutionSubGroup: React.FC<
  IAutoCompleteExecutionSubGroup
> = ({
  setIsLoading,
  handelOnChangeExecutionSubGroup,
  currentExecutionSubGroupId,
  sx,
  className,
  isDisabled = false,
  required,
  error,
  helperText,
  listLabel,
  placeholder,
  includeExecutionSubGroupId = false,
  lazyLoad = false,
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  const [executionSubGroupData, fetchExecutionSubGroup, isFetching] =
    useFetchExecutionSubGroup({
      setIsLoading,
      fetchOnMount: !lazyLoad,
    });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      fetchExecutionSubGroup();
    }
  }, [lazyLoad, hasOpened, fetchExecutionSubGroup]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isFetching || !hasOpened);

  return (
    <Autocomplete
      sx={sx}
      defaultValue={
        currentExecutionSubGroupId
          ? (executionSubGroupData || []).find(
              (option: any) => option.id === currentExecutionSubGroupId,
            )
          : null
      }
      options={executionSubGroupData || []}
      value={
        (executionSubGroupData || []).find(
          (option: any) => option.id === currentExecutionSubGroupId,
        ) || null
      }
      isOptionEqualToValue={(option: any, value: any) =>
        option?.id === value?.id
      }
      getOptionLabel={(option: any) => {
        if (!option) return "";
        const label =
          option.name || option.execution_sub_group_code || option.code || "";
        return includeExecutionSubGroupId
          ? `(${option.id ?? ""}) ${label}`
          : label;
      }}
      onOpen={handleOpen}
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          style={{ color: "#000000" }}
          {...params}
          label={
            <span>
              {listLabel ? listLabel : "Execution Sub Group"}
              {required ? <span className="required-asterisk">*</span> : ""}
            </span>
          }
          placeholder={placeholder ? placeholder : "Select..."}
          InputLabelProps={{
            shrink: true,
          }}
          error={!!error}
          helperText={helperText || ""}
        />
      )}
      className={className}
      loadingText="Loading..."
      onChange={(event, value) =>
        handelOnChangeExecutionSubGroup(event, value)
      }
      disabled={Boolean(isDisabled)}
    />
  );
};

export default AutoCompleteExecutionSubGroup;
